import type { Accessor, Component } from 'solid-js'
import { For, Show } from 'solid-js'
import type { Subscriber } from '../../features/subscribers/domain/models/subscriber'
import { SubscriberSortBy } from '../../features/subscribers/domain/models/subscriber'
import Table, { type TableColumn, TableStatus } from '../shared/components/Table'
import TablePagination from '../shared/components/TablePagination'
import UserDirectoryTableRow from './UserDirectoryTableRow'
import UserDirectorySkeletonRow from './UserDirectorySkeletonRow'

interface UserDirectoryTableProps {
  readonly subscribers: Subscriber[]
  readonly totalCount: number
  readonly page: Accessor<number>
  readonly pageSize: number
  readonly setPage: (page: number) => void
  readonly sortBy: SubscriberSortBy
  readonly sortOrder: 'asc' | 'desc'
  readonly onSort: (column: SubscriberSortBy, direction: 'asc' | 'desc') => void
  readonly isLoading: boolean
}

const sortByToColumnId: Record<string, string> = {
  [SubscriberSortBy.NAME]: 'user',
  [SubscriberSortBy.EMAIL]: 'email',
  [SubscriberSortBy.SUBSCRIPTION_PLAN]: 'plan',
  [SubscriberSortBy.SUBSCRIPTION_LEVEL]: 'level',
  [SubscriberSortBy.SUBSCRIPTION_STATUS]: 'status',
  [SubscriberSortBy.JOINED_AT]: 'date_added'
}

const columnIdToSortBy: Record<string, SubscriberSortBy> = {
  user: SubscriberSortBy.NAME,
  email: SubscriberSortBy.EMAIL,
  plan: SubscriberSortBy.SUBSCRIPTION_PLAN,
  level: SubscriberSortBy.SUBSCRIPTION_LEVEL,
  status: SubscriberSortBy.SUBSCRIPTION_STATUS,
  date_added: SubscriberSortBy.JOINED_AT
}

const UserDirectoryTable: Component<UserDirectoryTableProps> = (props) => {
  const tableOrderBy = () => {
    const colId = sortByToColumnId[props.sortBy]
    if (!colId) return undefined
    return `${colId}:${props.sortOrder}`
  }

  const setTableOrderBy = (orderBy: string | undefined): void => {
    if (!orderBy) {
      props.onSort(props.sortBy, 'desc')
      return
    }

    const [colId, direction] = orderBy.split(':')
    const sortByValue = columnIdToSortBy[colId]
    if (sortByValue && (direction === 'asc' || direction === 'desc')) {
      props.onSort(sortByValue, direction as 'asc' | 'desc')
    }
  }

  const tableColumns: TableColumn[] = [
    { id: 'user', sortable: true, class: 'w-56' },
    { id: 'email', sortable: true, class: 'w-64' },
    { id: 'username', class: 'w-48' },
    { id: 'region', class: 'w-48' },
    { id: 'plan', sortable: true, class: 'w-36' },
    { id: 'level', sortable: true, class: 'w-32' },
    { id: 'date_added', sortable: true, class: 'w-36' },
    { id: 'status', class: 'w-28' }
  ]

  return (
    <div class="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div class="border-t border-gray-200">
        <div class="overflow-x-auto">
          <Table
            status={() => TableStatus.Loaded}
            columns={tableColumns}
            orderBy={tableOrderBy}
            setOrderBy={setTableOrderBy}
            tableId="user_directory"
          >
            <Show
              when={props.subscribers.length > 0}
              fallback={
                <Show when={props.isLoading}>
                  <For each={Array.from({ length: props.pageSize })}>{() => <UserDirectorySkeletonRow />}</For>
                </Show>
              }
            >
              <For each={props.subscribers}>
                {(subscriber) => <UserDirectoryTableRow subscriber={subscriber} isLoading={props.isLoading} />}
              </For>
            </Show>
          </Table>
        </div>
        <Show when={props.totalCount > props.pageSize}>
          <TablePagination
            totalCount={props.totalCount}
            pageSize={props.pageSize}
            page={props.page}
            setPage={props.setPage}
          />
        </Show>
      </div>
    </div>
  )
}

export default UserDirectoryTable
